import { useContext, useEffect, useState, useCallback, useRef } from "react";
import EventCard from "../../components/Layout/Card/EventCard";
import Articles from "./Cards/Articles";
import Referrals2, { IReferralCard } from "./Cards/Referrals2";
import Press from "./Cards/Press";
import LatestNews from "./Cards/LatestNews";
import AuthContext from "../../store/AuthContext";
import { getPersonById } from "../../Services/Person";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import { IPerson } from "../../interfaces";
import { getLastContents } from "../../Services/Content";
import { getFirmReferrals } from "../../Services/Referral";

import { IPersonCard } from "../../components/Layout/Frames/ProfileContactFrame";
import { IFirmCard } from "../../components/Layout/Frames/FirmFrame";
import { getFirmById, getLogoByIds } from "../../Services/Firm";
import { useNavigate } from "react-router-dom";

import { getOfficeByFirm } from "../../Services/Office";
import { IOfficeNew } from "../../interfaces/models/IOffice";
import MyEvents from "./Cards/MyEvents";
import MainBox from "./MainBox";
import Blog, { IBlogCard } from "./Cards/Blog";
import { getBlogs } from "../../Services/Blog";
import GroupBox from "./GroupBox";
import Banner from "../../components/Layout/Banner/Banner";
import BacgrkoundImage from "../../assets/images&icons/bg-cta-2.jpg";
import { formatLocationText } from "../../utils/functions";
import { UseFirmList } from "../../hooks/Catalogs/useFirm";
import { getAOPsByFirm } from "../../Services/AreaOfPractice";

interface ICommun {
  title: string;
  index: number;
}
interface ICards extends ICommun {
  index: number;
  children?: React.ReactNode;
  title: string;
  buttons?: {
    name: string;
    action: () => void;
    color?: "primary" | "accent";
    className?: string;
  }[];
  treatSingleButtonAsPrimary?: boolean;
  heightClassName?: string;
}

const removeLeadingP = (value: string | undefined): string => {
  if (!value) {
    return "";
  }
  return value.startsWith("P") ? value.substring(1) : value;
};

const getLegacyId = (content: any): string => {
  if (content.legacyId) {
    return content.legacyId;
  }
  if (content.shortId) {
    return removeLeadingP(content.shortId);
  }
  if (content.id) {
    return content.id;
  }
  return "";
};

const ProfilePage = () => {
  const { loggedPerson, isAuthenticated, tokenDecoded } =
    useContext(AuthContext);
  const [presses, setPresses] = useState<any[]>([]);
  const [articles, setArticles] = useState<any[]>([]);
  const [firmData, setFirmData] = useState<IFirmCard>();
  const [blog, setBlog] = useState<IBlogCard[]>([]);
  const { firmData: _firmList } = UseFirmList();

  const [personCard, setPersonCard] = useState<IPersonCard>(
    loggedPerson as IPersonCard,
  );
  const navigate = useNavigate();

  const [referrals, setReferrals] = useState<IReferralCard[]>([]);
  const [rawReferrals, setRawReferrals] = useState<IReferralCard[]>([]);

  const isFetchingData = useRef(false);
  const isFetchingFirmData = useRef(false);
  const lastLoggedPersonId = useRef<string | undefined>();
  const lastFirmDataPersonId = useRef<string | undefined>();

  const [cards, setCards] = useState<ICards[]>([
    {
      index: 0,
      title: "REFERRALS & COLLABORATIONS",
      buttons: [
        { name: "View All", action: () => navigate("/manage_referrals.asp") },
        {
          name: "Submit",
          action: () => navigate("/manage_referrals.asp?action=add"),
        },
      ],
      children: (
        <Referrals2
          data={referrals}
          userFirmLogo={loggedPerson?.firmLogo}
          userFirmName={loggedPerson?.firmName}
        />
      ),
    },
    {
      index: 1,
      title: "EVENTS",
      buttons: [
        { name: "View All", action: () => navigate("/events") },
        {
          name: "My Events",
          action: () => navigate("/my-events"),
          color: "accent",
          className: "px-5 py-2.5 md:min-w-[170px] [&_img]:ml-3",
        },
      ],
      children: <MyEvents />,
    },
    {
      index: 3,
      title: "ARTICLES",
      children: <Articles data={articles} />,
      treatSingleButtonAsPrimary: articles.length === 0 ? false : true,
    },
    {
      index: 4,
      title: "BLOG",
      buttons: [
        { name: "View All", action: () => navigate("/blog/default.aspx") },
      ],
      children: <Blog data={blog} />,
    },
    {
      index: 6,
      title: "PRESS",
      buttons: [
        { name: "View All", action: () => navigate("/releases.asp") },
        {
          name: "Submit",
          action: () => navigate("/manage_releases.asp"),
        },
      ],
      children: <Press data={presses} />,
    },
    {
      index: 7,
      title: "LATEST NEWS",
      children: <LatestNews />,
      buttons: [{ name: "View All", action: () => navigate("/newsfeeds") }],
    },
  ]);

  const extractUniqueAuthorIds = (articles: any[]): string[] => {
    return [
      ...new Set(
        articles
          .map((article) => article.authors?.[0])
          .filter(Boolean) as string[],
      ),
    ];
  };

  const extractUniqueFirmIds = (articles: any[]): string[] => {
    const allFirmIds = articles.flatMap((article) => {
      if (!article.memberFirms || article.memberFirms.length === 0) {
        return [];
      }
      return article.memberFirms
        .map((firm: any) => (typeof firm === "string" ? firm : firm?.id))
        .filter(Boolean);
    });
    return [...new Set(allFirmIds)];
  };

  const extractFirmIdFromMemberFirm = (memberFirm: any): string | null => {
    return typeof memberFirm === "string" ? memberFirm : memberFirm?.id || null;
  };

  const formatAuthorName = (person?: IPerson): string => {
    if (!person) {
      return "";
    }
    return `${person.title ?? ""} ${person.name ?? ""} ${person.lastName ?? ""}`.trim();
  };

  const fetchPress = useCallback(async () => {
    try {
      const lastPresses = await getLastContents(3, "press", loggedPerson?.firm);
      const _press = await Promise.all(
        lastPresses.map(async (lastPress) => {
          if (!lastPress.authors?.[0]) {
            return {
              ...lastPress,
              legacyId: getLegacyId(lastPress),
              authorName: "Unknown Author",
              publishedDate: new Date(lastPress.publishedDate),
            };
          }
          const person = await getPersonById(lastPress.authors?.[0]);
          return {
            ...lastPress,
            legacyId: getLegacyId(lastPress),
            publishedDate: new Date(lastPress.publishedDate),
            authorName: `${person?.title ?? ""} ${person?.name ?? ""} ${person?.lastName ?? ""}`,
          };
        }),
      );
      setPresses(_press);
    } catch (error) {
      console.error("Error fetching Press:", error);
    }
  }, [loggedPerson?.firm]);

  const fetchArticles = useCallback(async () => {
    try {
      let allArticles = await getLastContents(100, "article", undefined);

      let lastArticles: any[] = [];

      if (
        !loggedPerson?.areasOfPractice ||
        loggedPerson.areasOfPractice.length === 0
      ) {
        lastArticles = allArticles
          .sort((a: any, b: any) => {
            const dateA = new Date(a.publishedDate).getTime();
            const dateB = new Date(b.publishedDate).getTime();
            return dateB - dateA;
          })
          .slice(0, 3);
      } else {
        const personAopIds = loggedPerson.areasOfPractice.map(
          (aop: any) => aop.id,
        );

        const allAops = await getAOPsByFirm(loggedPerson.firm);
        const personAops =
          allAops?.filter((aop: any) => personAopIds.includes(aop.id)) || [];

        const practiceIndustryIds = new Set<string>();
        personAops.forEach((aop: any) => {
          if (
            aop.practiceIndustryIds &&
            Array.isArray(aop.practiceIndustryIds)
          ) {
            aop.practiceIndustryIds.forEach((piId: string) =>
              practiceIndustryIds.add(piId),
            );
          }
        });

        const uniquePracticeIndustryIds = Array.from(practiceIndustryIds);

        if (uniquePracticeIndustryIds.length === 0) {
          lastArticles = allArticles
            .sort((a: any, b: any) => {
              const dateA = new Date(a.publishedDate).getTime();
              const dateB = new Date(b.publishedDate).getTime();
              return dateB - dateA;
            })
            .slice(0, 3);
        } else {
          const filteredArticles =
            allArticles?.filter((article: any) => {
              if (
                !article.practiceIndustries ||
                !Array.isArray(article.practiceIndustries)
              ) {
                return false;
              }
              return article.practiceIndustries.some((piId: string) =>
                uniquePracticeIndustryIds.includes(piId),
              );
            }) || [];

          lastArticles = filteredArticles
            .sort((a: any, b: any) => {
              const dateA = new Date(a.publishedDate).getTime();
              const dateB = new Date(b.publishedDate).getTime();
              return dateB - dateA;
            })
            .slice(0, 3);
        }
      }

      if (!lastArticles || lastArticles.length === 0) {
        setArticles([]);
        return;
      }

      const enrichArticleWithAuthor = (
        article: any,
        personMap: Map<string, IPerson>,
      ) => {
        const authorId = article.authors?.[0];
        if (!authorId) {
          return article;
        }

        const person = personMap.get(authorId);
        return {
          ...article,
          authorName: formatAuthorName(person),
        };
      };

      const enrichArticleWithFirmLogos = (
        article: any,
        firmLogoMap: Map<string, string>,
      ) => {
        if (!article.memberFirms || article.memberFirms.length === 0) {
          return { ...article, firmLogos: undefined };
        }

        const firmLogos = article.memberFirms
          .map(extractFirmIdFromMemberFirm)
          .filter((id: string | null): id is string => Boolean(id))
          .map((firmId: string) => firmLogoMap.get(firmId))
          .filter((logo: string | undefined): logo is string => Boolean(logo));

        return {
          ...article,
          firmLogos: firmLogos.length > 0 ? firmLogos : undefined,
        };
      };

      const authorIds = extractUniqueAuthorIds(lastArticles);
      const firmIds = extractUniqueFirmIds(lastArticles);

      const [persons, firmsData] = await Promise.all([
        authorIds.length > 0
          ? Promise.all(authorIds.map((id) => getPersonById(id)))
          : Promise.resolve([]),
        firmIds.length > 0 ? getLogoByIds(firmIds) : Promise.resolve([]),
      ]);

      const personMap = new Map(
        persons.map((person, index) => [authorIds[index], person]),
      );

      const firmLogoMap = new Map(
        firmsData.map((firm) => [firm.id, firm.firmLogo || firm.logo]),
      );

      const enrichedArticles = lastArticles
        .map((article) => enrichArticleWithAuthor(article, personMap))
        .map((article) => enrichArticleWithFirmLogos(article, firmLogoMap))
        .map((article) => ({
          ...article,
          legacyId: getLegacyId(article),
        }));

      setArticles(enrichedArticles);
    } catch (error: any) {
      console.error("Error fetching articles:", error);
      setArticles([]);
    }
  }, [loggedPerson]);

  const fetchReferrals = useCallback(async () => {
    if (!_firmList.length) {
      return;
    }
    try {
      if (!loggedPerson) {
        return;
      }

      const possibleId = loggedPerson.firmLegacyId;

      if (!possibleId) {
        return;
      }
      const result = await getFirmReferrals(Number(possibleId), 3);

      const referralCards = result.map((referral) => ({
        ...referral,
        legacyId: referral.legacyId ?? 0,
        firmById: referral.firmById?.toString(),
        firmToId: referral.firmToId?.toString(),
        firmByName:
          referral.firmByName ??
          _firmList.find(
            (firm: any) => firm.legacyId === Number(referral.firmById),
          )?.name,
        firmToName:
          referral.firmToName ??
          _firmList.find(
            (firm: any) => firm.legacyId === Number(referral.firmById),
          )?.name,
      }));
      setRawReferrals(referralCards);
    } catch (error) {
      console.error("Error fetching referrals:", error);
    }
  }, [loggedPerson, _firmList]);

  const fetchBlogs = useCallback(async () => {
    try {
      const blogData = await getBlogs();

      const sortedBlogs = blogData.sort((a, b) => {
        const dateA = new Date(a.dateCreated).getTime();
        const dateB = new Date(b.dateCreated).getTime();
        return dateB - dateA;
      });

      setBlog(sortedBlogs);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (isFetchingData.current) {
      return;
    }

    isFetchingData.current = true;
    try {
      await Promise.all([
        fetchPress(),
        fetchArticles(),
        fetchReferrals(),
        fetchBlogs(),
      ]);
    } catch (error: any) {
      console.error("Error in fetchData:", error);
    } finally {
      isFetchingData.current = false;
    }
  }, [fetchPress, fetchArticles, fetchReferrals, fetchBlogs]);

  useEffect(() => {
    if (!loggedPerson || !loggedPerson.id) {
      return;
    }

    if (lastLoggedPersonId.current === loggedPerson.id) {
      return;
    }

    lastLoggedPersonId.current = loggedPerson.id;
    fetchData();
  }, [loggedPerson, isAuthenticated, tokenDecoded, fetchData]);

  useEffect(() => {
    if (!presses.length) {
      return;
    }
    setCards((prevCards) =>
      prevCards.map((card) =>
        card.index === 6
          ? { ...card, children: <Press data={presses} /> }
          : card,
      ),
    );
  }, [presses]);

  useEffect(() => {
    if (!blog.length) {
      return;
    }
    setCards((prevCards) =>
      prevCards.map((card) =>
        card.index === 4 ? { ...card, children: <Blog data={blog} /> } : card,
      ),
    );
  }, [blog]);

  useEffect(() => {
    setCards((prevCards) =>
      prevCards.map((card) =>
        card.index === 3
          ? {
              ...card,
              children: <Articles data={articles} />,
              buttons:
                articles.length === 0
                  ? [
                      {
                        name: "Add an Article",
                        action: () => navigate("/submitarticle.asp?action=add"),
                      },
                    ]
                  : [
                      {
                        name: "View All",
                        action: () => navigate("/publications.asp"),
                      },
                      {
                        name: "Submit",
                        action: () => navigate("/manage_publications.asp"),
                      },
                    ],
            }
          : card,
      ),
    );
  }, [articles, navigate]);

  useEffect(() => {
    if (!rawReferrals.length || !_firmList.length) {
      if (rawReferrals.length) {
        setReferrals(rawReferrals);
      }
      return;
    }

    const enrichedReferrals = rawReferrals.map((referral) => ({
      ...referral,
      firmByName:
        referral.firmByName ??
        _firmList.find(
          (firm: any) => firm.legacyId === Number(referral.firmById),
        )?.name,
      firmToName:
        referral.firmToName ??
        _firmList.find(
          (firm: any) => firm.legacyId === Number(referral.firmToId),
        )?.name,
    }));

    setReferrals(enrichedReferrals);
  }, [rawReferrals, _firmList]);

  useEffect(() => {
    if (!referrals.length) return;
    setCards((prevCards) =>
      prevCards.map((card) =>
        card.index === 0
          ? {
              ...card,
              children: (
                <Referrals2
                  data={referrals}
                  userFirmLogo={loggedPerson?.firmLogo}
                  userFirmName={loggedPerson?.firmName}
                />
              ),
            }
          : card,
      ),
    );
  }, [referrals, loggedPerson?.firmLogo, loggedPerson?.firmName]);

  const getFirmAndOfficeData = useCallback(
    async (firmId: string, person: IPerson | undefined) => {
      if (isFetchingFirmData.current || !firmId || !person) {
        return;
      }

      isFetchingFirmData.current = true;
      try {
        const firmResponse = await getFirmById(firmId);
        const officesResponse: IOfficeNew[] = await getOfficeByFirm(
          firmResponse?.id ?? "",
        );
        const personMainOffice = officesResponse.find(
          (o) => o.id === person?.mainOffice,
        );

        setPersonCard({
          ...person,
          id: person?.id ?? "",
          photo: person?.photo ?? "",
          name: person?.name ?? "",
          lastName: person?.lastName ?? "",
          title: person?.title ?? "",
          listTitle: person?.listTitle ?? "",
          socialNetworks: person?.socialNetworks ?? [],
          city: personMainOffice?.cityName ?? "",
          country: personMainOffice?.countryName ?? "",
        });

        const mainOffice = officesResponse.find(
          (o) => String(o.officeType) === "Main",
        );
        console.log(officesResponse);
        console.log(mainOffice);

        setFirmData({
          ...firmResponse,
          mainOffice: formatLocationText(mainOffice as IOfficeNew),
          hasMoreOffices: officesResponse.length > 1,
        });
      } catch (error) {
        console.error("getFirmAndOfficeData - Error:", error);
      } finally {
        isFetchingFirmData.current = false;
      }
    },
    [],
  );

  useEffect(() => {
    if (
      !loggedPerson ||
      !loggedPerson.id ||
      !loggedPerson.firm ||
      lastFirmDataPersonId.current === loggedPerson.id
    ) {
      return;
    }

    lastFirmDataPersonId.current = loggedPerson.id;

    getFirmAndOfficeData(loggedPerson.firm, loggedPerson);
  }, [loggedPerson, getFirmAndOfficeData]);

  const { patchUserPreferences } = useUserPreferences();
  const userCardOrder = loggedPerson?.preferences?.myWSGOrderCards;

  useEffect(() => {
    if (!userCardOrder?.length) {
      return;
    }

    setCards((prevCards) => {
      const orderedCards = userCardOrder
        .map((index) => prevCards.find((card) => card.index === index))
        .filter((card): card is ICards => card !== undefined);

      return orderedCards.length > 0 ? orderedCards : prevCards;
    });
  }, [userCardOrder]);

  const onDragStart = (
    event: React.DragEvent<HTMLDivElement>,
    position: number,
  ) => {
    event.dataTransfer.setData("text/plain", position.toString());
  };

  const onDrop = (
    event: React.DragEvent<HTMLDivElement>,
    newPosition: number,
  ) => {
    event.preventDefault();
    const draggedPosition = parseInt(
      event.dataTransfer.getData("text/plain"),
      10,
    );

    const updatedCards = [...cards];
    const temp = updatedCards[draggedPosition];
    updatedCards[draggedPosition] = updatedCards[newPosition];
    updatedCards[newPosition] = temp;

    setCards(updatedCards);
    savePreferences(updatedCards.map((card) => card.index));
  };

  const savePreferences = async (orderCards: number[]) => {
    if (!loggedPerson) {
      return;
    }
    await patchUserPreferences({
      preferences: {
        ...loggedPerson.preferences,
        myWSGOrderCards: orderCards,
      },
    });
  };

  return (
    <div className={`flex flex-col gap-6`}>
      <MainBox personCard={personCard} firmData={firmData as IFirmCard} />

      <GroupBox />

      <div className="grid grid-cols-2 items-stretch gap-8">
        {cards.map((card, index) => (
          <EventCard
            key={index}
            title={card.title}
            index={index}
            onDragStart={onDragStart}
            onDrop={onDrop}
            buttons={card.buttons}
            treatSingleButtonAsPrimary={card.treatSingleButtonAsPrimary ?? true}
            heightClassName={card.heightClassName}
          >
            {card.children}
          </EventCard>
        ))}
        <Banner
          title="Up Your Expertise"
          description="View one of many WSG informative sessions to get the best value out of your firm's membership and digital profile."
          backgroundImage={BacgrkoundImage}
          buttonText="View Webinars"
          buttonClick={() => navigate("/meetings?past=true&etype=webinar")}
        />
      </div>
    </div>
  );
};

export default ProfilePage;
